import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Tooltip,
  useTheme,
  Chip,
  withStyles
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { BarChart, PieChart } from '@material-ui/icons';
import { appStyles } from '../../styles';

const CNX_CONFIG = [
  { key: 'MX_CNX', label: 'MX', color: '#f44336' },
  { key: 'WX_CNX', label: 'WX', color: '#ff9800' },
  { key: 'OPS_CNX', label: 'OPS', color: '#9c27b0' },
  { key: 'USER_CNX', label: 'USER', color: '#795548' },
];

const HOURS_CONFIG = [
  { key: 'flown', label: 'Flown', color: '#4caf50' }, // success/green
  { key: 'lostCancelled', label: 'Lost (Cnx)', color: '#f44336' }, // error/red
  { key: 'lostUnderfly', label: 'Lost (Under)', color: '#ff9800' }, // warning/orange
  { key: 'gainedOverfly', label: 'Gained (Over)', color: '#2196f3' }, // info/blue
  { key: 'upcoming', label: 'Upcoming', color: '#9e9e9e' }, // grey
];

const UNKNOWN_COLOR = '#9e9e9e';

const StatsChartView = ({ data, showDetailedCnx, isHours, classes }) => {
  const theme = useTheme();
  const [view, setView] = useState('pie');
  const [selectedTypes, setSelectedTypes] = useState(Object.keys(data || {}));

  if (!data || Object.keys(data).length === 0) return null;

  const handleViewChange = (event, nextView) => {
    if (nextView !== null) {
      setView(nextView);
    }
  };

  const toggleType = (type) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type) 
        : [...prev, type]
    );
  };

  const sortedEntries = Object.entries(data)
    .filter(([type]) => selectedTypes.includes(type))
    .sort(([a], [b]) => a.localeCompare(b));

  const maxVal = isHours 
    ? Math.max(...Object.values(data).map(s => (s.hours?.flown || 0) + (s.hours?.lostCancelled || 0) + (s.hours?.lostUnderfly || 0) + (s.hours?.gainedOverfly || 0) + (s.hours?.upcoming || 0)), 1)
    : Math.max(...Object.values(data).map(s => s.total), 1);
  
  let scaleMax;
  if (maxVal <= 5) scaleMax = 5;
  else if (maxVal <= 10) scaleMax = 10;
  else scaleMax = Math.ceil(maxVal / 10) * 10;

  const getTicks = () => {
    if (scaleMax <= 5) return [0, 1, 2, 3, 4, 5];
    if (scaleMax === 10) return [0, 2, 4, 6, 8, 10];
    return [0, 0.25, 0.5, 0.75, 1].map(p => Math.round(p * scaleMax));
  };
  const ticks = getTicks();

  const totalCompleted = sortedEntries.reduce((sum, [_, s]) => sum + s.completed, 0);
  const totalUnknown = sortedEntries.reduce((sum, [_, s]) => sum + (s.unknown || 0), 0);
  const totalCancelled = sortedEntries.reduce((sum, [_, s]) => sum + s.cancelled, 0);
  const totalInView = totalCompleted + totalUnknown + totalCancelled;

  const hoursInView = {
    flown: sortedEntries.reduce((sum, [_, s]) => sum + (s.hours?.flown || 0), 0),
    lostCancelled: sortedEntries.reduce((sum, [_, s]) => sum + (s.hours?.lostCancelled || 0), 0),
    lostUnderfly: sortedEntries.reduce((sum, [_, s]) => sum + (s.hours?.lostUnderfly || 0), 0),
    gainedOverfly: sortedEntries.reduce((sum, [_, s]) => sum + (s.hours?.gainedOverfly || 0), 0),
    upcoming: sortedEntries.reduce((sum, [_, s]) => sum + (s.hours?.upcoming || 0), 0),
  };
  const totalHoursInView = hoursInView.flown + hoursInView.lostCancelled + hoursInView.lostUnderfly + hoursInView.gainedOverfly + hoursInView.upcoming;

  const getCoordinatesForPercent = (percent) => {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  };

  const renderPieSlice = (value, total, color, startPercent, label) => {
    if (value <= 0 || total === 0) return null;
    const percent = value / total;
    const [startX, startY] = getCoordinatesForPercent(startPercent);
    const [endX, endY] = getCoordinatesForPercent(startPercent + percent);
    const largeArcFlag = percent > 0.5 ? 1 : 0;
    const pathData = [
      `M ${startX} ${startY}`,
      `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
      `L 0 0`,
    ].join(' ');

    return (
      <Tooltip key={label} title={`${label}: ${value.toFixed(1)} (${(percent * 100).toFixed(1)}%)`}>
        <path d={pathData} fill={color} />
      </Tooltip>
    );
  };

  return (
    <Box className={classes.statsChartContainer}>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          aria-label="chart view"
          size="small"
        >
          <ToggleButton value="bar" aria-label="bar chart">
            <BarChart />
          </ToggleButton>
          <ToggleButton value="pie" aria-label="pie chart">
            <PieChart />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {view === 'bar' ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom align="center">{isHours ? 'Hours Breakdown' : 'Success vs. Cancellation'}</Typography>
          
          <Box className={classes.statsChartArea}>
            <Box className={classes.statsScaleContainer}>
              {ticks.map((tickValue, index) => {
                const position = (tickValue / scaleMax) * 100;
                return (
                  <Box key={index} className={classes.statsScaleTickWrapper} style={{ bottom: `${position}%` }}>
                    <Typography className={classes.statsScaleLabel}>{tickValue}</Typography>
                    <Box className={classes.statsScaleTick} />
                  </Box>
                );
              })}
            </Box>

            <Box className={classes.statsBarArea}>
              {sortedEntries.map(([type, stats]) => {
                if (isHours) {
                  const h = stats.hours;
                  let currentBottom = 0;
                  return (
                    <Box key={type} className={classes.statsBarWrapper}>
                      <Tooltip title={`${type} - Flown: ${h.flown.toFixed(1)}, Lost Cnx: ${h.lostCancelled.toFixed(1)}, Lost Under: ${h.lostUnderfly.toFixed(1)}, Gained: ${h.gainedOverfly.toFixed(1)}, Upcoming: ${h.upcoming.toFixed(1)}`}>
                        <Box className={classes.statsBarTrack}>
                          {HOURS_CONFIG.map(config => {
                            const val = h[config.key] || 0;
                            const height = (val / scaleMax) * 100;
                            const bottom = currentBottom;
                            currentBottom += height;
                            return (
                              <Box 
                                key={config.key}
                                className={classes.statsBarSegment}
                                style={{ 
                                  height: `${height}%`, 
                                  bottom: `${bottom}%`,
                                  backgroundColor: config.color
                                }} 
                              />
                            );
                          })}
                        </Box>
                      </Tooltip>
                      <Typography className={classes.statsVerticalLabel} title={type}>{type}</Typography>
                    </Box>
                  );
                }

                const completedHeight = (stats.completed / scaleMax) * 100;
                const unknownHeight = ((stats.unknown || 0) / scaleMax) * 100;
                let currentBottom = completedHeight + unknownHeight;
                
                return (
                  <Box key={type} className={classes.statsBarWrapper}>
                    <Tooltip title={`${type} - Completed: ${stats.completed}, Unknown: ${stats.unknown || 0}, Cancelled: ${stats.cancelled}`}>
                      <Box className={classes.statsBarTrack}>
                        <Box 
                          className={classes.statsBarFill} 
                          style={{ height: `${completedHeight}%` }} 
                        />
                        <Box 
                          className={classes.statsBarSegment} 
                          style={{ 
                            height: `${unknownHeight}%`, 
                            bottom: `${completedHeight}%`,
                            backgroundColor: UNKNOWN_COLOR
                          }} 
                        />
                        
                        {showDetailedCnx ? (
                          CNX_CONFIG.map(config => {
                            const count = stats.byStatus[config.key] || 0;
                            const height = (count / scaleMax) * 100;
                            const bottom = currentBottom;
                            currentBottom += height;
                            return (
                              <Box 
                                key={config.key}
                                className={classes.statsBarSegment}
                                style={{ 
                                  height: `${height}%`, 
                                  bottom: `${bottom}%`,
                                  backgroundColor: config.color
                                }} 
                              />
                            );
                          })
                        ) : (
                          <Box 
                            className={classes.statsBarSegment}
                            style={{ 
                              height: `${(stats.cancelled / scaleMax) * 100}%`, 
                              bottom: `${completedHeight + unknownHeight}%`,
                              backgroundColor: theme.palette.error.light
                            }} 
                          />
                        )}
                      </Box>
                    </Tooltip>
                    <Typography className={classes.statsVerticalLabel} title={type}>{type}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box mt={4} display="flex" justifyContent="center" flexWrap="wrap">
            {isHours ? (
              HOURS_CONFIG.map(config => (
                <Box key={config.key} className={classes.statsLegendItem}>
                  <Box className={classes.statsColorBox} style={{ backgroundColor: config.color }} />
                  <Typography variant="caption">{config.label}</Typography>
                </Box>
              ))
            ) : (
              <>
                <Box className={classes.statsLegendItem}>
                  <Box className={classes.statsColorBox} style={{ backgroundColor: theme.palette.primary.main }} />
                  <Typography variant="caption">Completed</Typography>
                </Box>
                <Box className={classes.statsLegendItem}>
                  <Box className={classes.statsColorBox} style={{ backgroundColor: UNKNOWN_COLOR }} />
                  <Typography variant="caption">Unknown</Typography>
                </Box>
                {showDetailedCnx ? (
                  CNX_CONFIG.map(config => (
                    <Box key={config.key} className={classes.statsLegendItem}>
                      <Box className={classes.statsColorBox} style={{ backgroundColor: config.color }} />
                      <Typography variant="caption">{config.label}</Typography>
                    </Box>
                  ))
                ) : (
                  <Box className={classes.statsLegendItem}>
                    <Box className={classes.statsColorBox} style={{ backgroundColor: theme.palette.error.light }} />
                    <Typography variant="caption">Cancelled</Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      ) : (
        <Box className={classes.statsPieContainer}>
          <Box className={classes.statsChipContainer}>
            {Object.keys(data).sort().map(type => (
              <Chip
                key={type}
                label={type}
                onClick={() => toggleType(type)}
                color={selectedTypes.includes(type) ? "primary" : "default"}
                variant={selectedTypes.includes(type) ? "default" : "outlined"}
                size="small"
              />
            ))}
          </Box>
          <Typography variant="subtitle2" gutterBottom>{isHours ? 'Hours Distribution' : 'Status Distribution'} (Selected Types)</Typography>
          {isHours ? (
            totalHoursInView > 0 ? (
              <>
                <svg viewBox="-1 -1 2 2" style={{ transform: 'rotate(-90deg)', width: '200px', height: '200px' }}>
                  {HOURS_CONFIG.reduce((acc, config) => {
                    const val = hoursInView[config.key] || 0;
                    if (val > 0) {
                      acc.elements.push(renderPieSlice(val, totalHoursInView, config.color, acc.currentPercent, config.label));
                      acc.currentPercent += val / totalHoursInView;
                    }
                    return acc;
                  }, { currentPercent: 0, elements: [] }).elements}
                </svg>
                <Box mt={3} display="flex" justifyContent="center" flexWrap="wrap">
                  {HOURS_CONFIG.map(config => (
                    <Box key={config.key} className={classes.statsLegendItem}>
                      <Box className={classes.statsColorBox} style={{ backgroundColor: config.color }} />
                      <Typography variant="caption">{config.label} ({hoursInView[config.key].toFixed(1)})</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">No data for selected types</Typography>
            )
          ) : (
            totalInView > 0 ? (
              <>
                <svg viewBox="-1 -1 2 2" style={{ transform: 'rotate(-90deg)', width: '200px', height: '200px' }}>
                  {renderPieSlice(totalCompleted, totalInView, theme.palette.primary.main, 0, 'Completed')}
                  {renderPieSlice(totalUnknown, totalInView, UNKNOWN_COLOR, totalCompleted / totalInView, 'Unknown')}
                  
                  {showDetailedCnx ? (
                    CNX_CONFIG.reduce((acc, config) => {
                      const count = sortedEntries.reduce((sum, [_, s]) => sum + (s.byStatus[config.key] || 0), 0);
                      if (count > 0) {
                        acc.elements.push(renderPieSlice(count, totalInView, config.color, acc.currentPercent, config.label));
                        acc.currentPercent += count / totalInView;
                      }
                      return acc;
                    }, { currentPercent: (totalCompleted + totalUnknown) / totalInView, elements: [] }).elements
                  ) : (
                    renderPieSlice(totalCancelled, totalInView, theme.palette.error.light, (totalCompleted + totalUnknown) / totalInView, 'Cancelled')
                  )}
                </svg>
                <Box mt={3} display="flex" justifyContent="center" flexWrap="wrap">
                  <Box className={classes.statsLegendItem}>
                    <Box className={classes.statsColorBox} style={{ backgroundColor: theme.palette.primary.main }} />
                    <Typography variant="caption">Completed ({totalCompleted})</Typography>
                  </Box>
                  <Box className={classes.statsLegendItem}>
                    <Box className={classes.statsColorBox} style={{ backgroundColor: UNKNOWN_COLOR }} />
                    <Typography variant="caption">Unknown ({totalUnknown})</Typography>
                  </Box>
                  {showDetailedCnx ? (
                    CNX_CONFIG.map(config => {
                      const count = sortedEntries.reduce((sum, [_, s]) => sum + (s.byStatus[config.key] || 0), 0);
                      return count > 0 ? (
                        <Box key={config.key} className={classes.statsLegendItem}>
                          <Box className={classes.statsColorBox} style={{ backgroundColor: config.color }} />
                          <Typography variant="caption">{config.label} ({count})</Typography>
                        </Box>
                      ) : null;
                    })
                  ) : (
                    <Box className={classes.statsLegendItem}>
                      <Box className={classes.statsColorBox} style={{ backgroundColor: theme.palette.error.light }} />
                      <Typography variant="caption">Cancelled ({totalCancelled})</Typography>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">No data for selected types</Typography>
            )
          )}
        </Box>
      )}
    </Box>
  );
};

export default withStyles(appStyles)(StatsChartView);
